import { PropertyDefinitions } from 'hs-resources/resources/PropertyDefinition';
import { useResource } from 'pulse/react';
import { useCallback, useMemo } from 'react';
import memoizeOne from 'react-utils/memoizeOne';
import { useHttpClient } from '../../client/HttpClientContext';
import { useIsPulseEnabled } from './useIsPulseEnabled';
import { getPropertyDefinitions } from '../../propertyDefinitions/getPropertyDefinitions';
import { useAsyncFunction } from '../../utils/hooks/useAsyncFunction';
import { keyBy } from '../../utils/keyBy';
import { usePropertyDefinitionOverride } from '../context/PropertyDefinitionOverrideContext';
import { useObjectTypeId } from './useObjectTypeId';
import { useProperty } from './useProperty';
import { getOwnProperty } from '../../utils/getOwnProperty';
const defaultResourceData = [];
const defaultDataByName = {};
const keyByName = memoizeOne(properties => keyBy(properties, ({
  property: {
    name
  }
}) => name));
export const usePropertyDefinitionByName = (objectTypeId, options = {
  skip: false
}) => {
  const propertyDefinitionOverride = usePropertyDefinitionOverride();
  const httpClient = useHttpClient();
  const isPulseEnabled = useIsPulseEnabled();
  const {
    data: resourceData = defaultResourceData,
    loading: resourceLoading,
    error: resourceError
  } = useResource(PropertyDefinitions, {
    // Plumb the context-injected httpClient through to the resolver. Pulse's
    // upcoming http-client-agnostic refactor (PR HubSpotEngineering/pulse#186)
    // turns createClient() into a singleton, so the client-level httpClient
    // can no longer carry per-consumer overrides. Embedded callers like
    // SalesSidebar inject a custom apiClient via HttpClientContext; without
    // this per-watch override they'd silently get the singleton's httpClient.
    httpClient,
    skip: !isPulseEnabled || options.skip,
    params: {
      objectTypeId,
      showHighlySensitiveProperties: true
    }
  });
  const resourceDataByName = useMemo(() => {
    return keyByName(resourceData);
  }, [resourceData]);
  const getDefinitions = useCallback(() => {
    return getPropertyDefinitions({
      objectTypeId,
      httpClient
    }).then(keyByName);
  }, [httpClient, objectTypeId]);
  const {
    data: dataByName = defaultDataByName,
    error: dataError,
    loading: dataLoading
  } = useAsyncFunction(getDefinitions, Object.assign({}, options, {
    skip: isPulseEnabled || options.skip
  }));
  let propertyDefinitionsByName = isPulseEnabled ? resourceDataByName : dataByName;
  const error = isPulseEnabled ? resourceError || undefined : dataError;
  const loading = isPulseEnabled ? resourceLoading : dataLoading;
  if (propertyDefinitionOverride) {
    // We need to copy here because we cannot mutate pulse resources.
    propertyDefinitionsByName = Object.assign({}, propertyDefinitionsByName, {
      [propertyDefinitionOverride.property.name]: propertyDefinitionOverride
    });
  }
  return {
    propertyDefinitionsByName,
    error,
    loading
  };
};

/**
 * Fetches the property definition for the current property or a specific property by name.
 */
export const usePropertyDefinition = ({
  skip,
  propertyName
} = {}) => {
  const property = useProperty();
  const objectTypeId = useObjectTypeId();
  const {
    propertyDefinitionsByName = {},
    error,
    loading
  } = usePropertyDefinitionByName(objectTypeId, {
    skip
  });
  const targetPropertyName = propertyName || property.name;
  const propertyDefinition = getOwnProperty(propertyDefinitionsByName, targetPropertyName);
  return {
    propertyDefinition,
    error,
    loading
  };
};
export const usePropertyDefinitionsByObjectTypeId = (objectTypeIds, options = {
  skip: false
}) => {
  const httpClient = useHttpClient();
  const stringifiedObjectTypeIds = objectTypeIds.join(',');

  // getPropertyDefinitions caches, but we don't want to repeat the keying operation, so
  // we maintain a local cache to avoid the extra work.
  const definitionsCache = useMemo(() => new Map(), []);
  const getDefinitions = useCallback(() => {
    const uncachedIds = objectTypeIds.filter(id => !definitionsCache.has(id));
    return Promise.all(uncachedIds.map(objectTypeId => getPropertyDefinitions({
      objectTypeId,
      httpClient
    }).then(definitions => {
      definitionsCache.set(objectTypeId, keyByName(definitions));
    }))).then(() => {
      return objectTypeIds.reduce((acc, objectTypeId) => {
        acc[objectTypeId] = definitionsCache.get(objectTypeId);
        return acc;
      }, {});
    });
    // we shallow compare the object type ids to avoid breaking the memoization when array reference changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [httpClient, stringifiedObjectTypeIds, definitionsCache]);
  return useAsyncFunction(getDefinitions, options);
};